import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PageHeader, QuickFilters, DataTable, Tag, Button, Sheet, Banner, Chip, useCkToast, ckConfirm,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import ImportSectionNext from '../../components/ImportSectionNext.jsx';
import KolelCardNext from './KolelCardNext.jsx';
import { useQuickCreate } from '../../next/quickCreate';
import { apiError } from '../../next/ui';

/**
 * כוללים — new design. Same endpoints as KolelsList: list, delete, audio play / upload per kolel,
 * the kolel card (side sheet), Excel import + partial update, and linking kolels to members.
 */
const STATUS_LABELS = { active: 'פעיל', inactive: 'לא פעיל' };

export default function KolelsListNext() {
  const toast = useCkToast();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quick, setQuick] = useState('all');
  const [uploadingId, setUploadingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const audioElRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadTarget = useRef(null);
  useQuickCreate(() => setSelectedId('new'));

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get('/kolels', { params: { page: 1, pageSize: 100000 } });
      setAllData(res.data || []);
    } catch (e) {
      setError(apiError(e, 'שגיאה בשליפת כוללים'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => () => { audioElRef.current?.pause(); }, []);

  const play = useCallback((kolelId) => {
    if (playingId === kolelId && audioElRef.current) { audioElRef.current.pause(); audioElRef.current = null; setPlayingId(null); return; }
    audioElRef.current?.pause();
    audioElRef.current = null;
    api.get(`/kolels/${kolelId}/audio`, { responseType: 'blob' })
      .then(({ data: blob }) => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); audioElRef.current = null; };
        audio.onerror = () => { setPlayingId(null); URL.revokeObjectURL(url); audioElRef.current = null; };
        audioElRef.current = audio;
        setPlayingId(kolelId);
        audio.play();
      })
      .catch(() => setPlayingId(null));
  }, [playingId]);

  const pickAudio = (kolelId) => { uploadTarget.current = kolelId; fileInputRef.current?.click(); };
  const uploadAudio = async (file) => {
    const kolelId = uploadTarget.current;
    if (!file || !kolelId) return;
    const fd = new FormData();
    fd.append('audio', file);
    setUploadingId(kolelId);
    try {
      const { data: res } = await api.post(`/kolels/${kolelId}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAllData((prev) => prev.map((r) => (r._id === kolelId ? { ...r, audioFilename: res.audioFilename, audio: r.audio || 'synced' } : r)));
      toast.success('קובץ השמע הועלה');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהעלאת קובץ שמע'));
    } finally {
      setUploadingId(null);
      uploadTarget.current = null;
    }
  };

  const remove = async (row) => {
    if (!(await ckConfirm({ title: 'מחיקת כולל', message: `למחוק את הכולל "${row.name}"?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/kolels/${row._id}`);
      setAllData((prev) => prev.filter((r) => r._id !== row._id));
      toast.success('הכולל נמחק');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקה'));
    }
  };

  const linkKolels = async () => {
    setLinkLoading(true);
    setLinkResult(null);
    try {
      const { data } = await api.post('/import/link-kolels');
      setLinkResult(data);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בקישור כוללים'));
    } finally {
      setLinkLoading(false);
    }
  };

  const counts = useMemo(() => ({
    all: allData.length,
    active: allData.filter((r) => r.status === 'active').length,
    inactive: allData.filter((r) => r.status !== 'active').length,
    noAudio: allData.filter((r) => !r.audioFilename).length,
  }), [allData]);
  const rows = useMemo(() => allData.filter((r) => (quick === 'active' ? r.status === 'active' : quick === 'inactive' ? r.status !== 'active' : quick === 'noAudio' ? !r.audioFilename : true)), [allData, quick]);
  const dash = (v) => (v == null || v === '' ? <span className="ck-muted">—</span> : v);

  return (
    <>
      <input ref={fileInputRef} type="file" accept="audio/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadAudio(f); }} />
      <PageHeader
        title="כוללים"
        sub={`${allData.length.toLocaleString('he-IL')} כוללים`}
        primary={{ label: 'כולל חדש', onClick: () => setSelectedId('new') }}
        more={[
          { key: 'link', label: linkLoading ? 'משייך…' : 'שיוך כוללים לכל החברים', icon: 'link', onClick: linkKolels, disabled: linkLoading },
          { key: 'refresh', label: 'רענון', icon: 'refresh', onClick: loadData },
        ]}
      />
      {linkResult ? (
        <Banner tone={linkResult.notFound?.length ? 'warning' : 'success'} icon="link" onClose={() => setLinkResult(null)}>
          {linkResult.message}
          {linkResult.notFound?.length ? <div className="nx-item__tags" style={{ marginTop: 6 }}>{linkResult.notFound.map((n) => <Chip key={n} tone="warning">{n}</Chip>)}</div> : null}
        </Banner>
      ) : null}
      <QuickFilters
        value={quick}
        onChange={(k) => setQuick((c) => (c === k ? 'all' : k))}
        loading={loading && !allData.length}
        items={[
          { key: 'all', label: 'כל הכוללים', value: counts.all, icon: 'building', tone: 'neutral' },
          { key: 'active', label: 'פעילים', value: counts.active, icon: 'check', tone: 'success' },
          { key: 'inactive', label: 'לא פעילים', value: counts.inactive, icon: 'pause', tone: 'neutral' },
          { key: 'noAudio', label: 'בלי קובץ שמע', value: counts.noAudio, icon: 'mic', tone: 'warning' },
        ]}
      />
      <DataTable
        mode="client"
        rows={rows}
        rowKey="_id"
        loading={loading}
        error={error}
        onRetry={loadData}
        onRefresh={loadData}
        storageKey="bchinot-kolels"
        layoutDefaults={{ pinned: ['name'] }}
        defaultQuery={{ sort: { key: 'code', dir: 1 } }}
        searchPlaceholder="חיפוש לפי שם, עיר, קוד, ראש כולל או גבאי…"
        exportFilename="כוללים"
        emptyTitle="לא נמצאו כוללים"
        onRowClick={(r) => setSelectedId(r._id)}
        actionsWidth={120}
        rowActions={(r) => [
          r.audioFilename ? { key: 'play', icon: playingId === r._id ? 'pause' : 'play', label: playingId === r._id ? 'עצירה' : 'השמעת שם הכולל', onClick: () => play(r._id) } : null,
          { key: 'up', icon: uploadingId === r._id ? 'refresh' : 'upload', label: r.audioFilename ? 'החלפת קובץ שמע' : 'העלאת קובץ שמע', onClick: () => pickAudio(r._id) },
        ].filter(Boolean)}
        rowMenu={(r) => [
          { key: 'open', label: 'פתיחת כרטיס', icon: 'edit', onClick: () => setSelectedId(r._id) },
          { key: 'audio', label: r.audioFilename ? 'החלפת קובץ שמע' : 'העלאת קובץ שמע', icon: 'upload', onClick: () => pickAudio(r._id) },
          { divider: true },
          { key: 'del', label: 'מחיקה', icon: 'trash', danger: true, onClick: () => remove(r) },
        ]}
        bulkActions={[]}
        columns={[
          { key: 'code', label: 'קוד', type: 'number', width: 80, render: (r) => (r.code != null ? <span className="nx-num">{r.code}</span> : dash()) },
          { key: 'name', label: 'שם כולל', width: 180, render: (r) => <b>{r.name}</b> },
          { key: 'city', label: 'עיר', type: 'enum' },
          { key: 'schedule', label: 'סדר', type: 'enum', render: (r) => dash(r.schedule) },
          { key: 'subject', label: 'מקצוע', type: 'enum', render: (r) => dash(r.subject) },
          { key: 'headFirstName', label: 'שם ראש כולל', render: (r) => dash(r.headFirstName) },
          { key: 'headLastName', label: 'משפחה', render: (r) => dash(r.headLastName) },
          { key: 'gabaiName', label: 'גבאי', render: (r) => dash(r.gabaiName) },
          { key: 'gabaiPhone', label: 'טלפון גבאי', ltr: true, copy: true, render: (r) => dash(r.gabaiPhone) },
          { key: 'memberCount', label: 'אברכים', type: 'number', render: (r) => dash(r.memberCount) },
          { key: 'status', label: 'סטטוס', type: 'enum', getValue: (r) => STATUS_LABELS[r.status] || r.status, render: (r) => <Tag tone={r.status === 'active' ? 'success' : 'neutral'}>{STATUS_LABELS[r.status] || r.status}</Tag> },
          { key: 'audioFilename', label: 'קובץ שמע', getValue: (r) => r.audioFilename || 'אין', render: (r) => (r.audioFilename ? <Tag tone="info" icon="mic" title={r.audioFilename}>יש</Tag> : <Tag>אין</Tag>) },
        ]}
        renderMobileRow={(r) => (
          <div className="nx-item__main">
            <b>{r.code != null ? `${r.code} · ` : ''}{r.name}</b>
            <small>{[r.city, r.schedule, r.gabaiName].filter(Boolean).join(' · ')}</small>
          </div>
        )}
      />

      <ImportSectionNext
        label="כוללים"
        endpoint="/import/kolels"
        templateEndpoint="/import/template/kolels"
        description="שם כולל, עיר, קוד, שם ראש כולל, משפחה, סדר, מקצוע, סימנים, שם גבאי, טלפון גבאי. ניתן גם להעלות קובץ ישן עם שורות הפרדה לפי עיר."
        onImportDone={() => loadData()}
        entityType="kolels"
        identifierColumns={[{ field: 'code', label: 'קוד כולל' }, { field: 'name', label: 'שם כולל' }]}
        extraActions={(
          <Banner tone="info" icon="link" action={<Button size="sm" variant="primary" icon="link" disabled={linkLoading} onClick={linkKolels}>{linkLoading ? 'משייך…' : 'שייך כוללים לכל החברים'}</Button>}>
            <b>שיוך כוללים לחברים אוטומטי</b> — מקשר בין שם הכולל הגולמי מהאקסל לבין הכוללים שיובאו למערכת. צריך להריץ אחרי ייבוא הכוללים.
          </Banner>
        )}
      />

      <Sheet open={!!selectedId} onClose={() => setSelectedId(null)} width={980} ariaLabel="כרטיס כולל">
        {selectedId ? (
          <div className="nx-sheet-body">
            <KolelCardNext
              key={selectedId}
              recordId={selectedId}
              onClose={(reason, opts) => {
                if (reason === 'created' || reason === 'saved') loadData();
                if (!opts?.keepOpen) setSelectedId(null);
              }}
            />
          </div>
        ) : null}
      </Sheet>
    </>
  );
}
